import { useState, useEffect, useRef } from 'react';
import { refreshAccessToken } from '@store/feature/chat/chatApi';
import { useDispatch } from 'react-redux';
import {
    invalidateChatGroup,
    invalidateChatMessages,
    invalidateUnreadMessageCount,
    useGetChatProfileQuery
} from '../store/feature/chat/chatApi';

const MAX_RECONNECT_DELAY = 60000;
const INITIAL_RECONNECT_DELAY = 1000;

const useWS = (url) => {
    const dispatch = useDispatch();
    const { data: chatUser } = useGetChatProfileQuery();
    const [isConnected, setIsConnected] = useState(false);
    const webSocketRef = useRef(null);
    const reconnectDelayRef = useRef(INITIAL_RECONNECT_DELAY);
    const keepRetryingRef = useRef(true);
    const tokenRef = useRef(localStorage.getItem('token'));

    const refreshChatData = () => {
        invalidateChatMessages(dispatch);
        invalidateChatGroup(dispatch);
        invalidateUnreadMessageCount(dispatch);
    };

    const connect = (token) => {
        if (!token) {
            console.error('No token available. Cannot connect WebSocket.');
            return;
        }

        const ws = new WebSocket(`${url}?token=${token}`);

        ws.onopen = () => {
            console.log('WebSocket connected');
            setIsConnected(true);
            reconnectDelayRef.current = INITIAL_RECONNECT_DELAY;
            refreshChatData();
        };

        ws.onclose = () => {
            console.warn('WebSocket disconnected');
            setIsConnected(false);
            reconnect();
        };

        ws.onerror = (error) => {
            console.error('WebSocket error:', error);
        };

        webSocketRef.current = ws;
    };

    const reconnect = async () => {
        if (!keepRetryingRef.current) {
            console.warn('Reconnection stopped.');
            return;
        }

        if (reconnectDelayRef.current >= MAX_RECONNECT_DELAY) {
            console.error('Reached max reconnection delay.');
            keepRetryingRef.current = false;
            return;
        }

        console.log(`Reconnecting in ${reconnectDelayRef.current / 1000} seconds...`);

        setTimeout(async () => {
            try {
                const tokenResp = await refreshAccessToken();

                if (tokenResp?.error) {
                    console.error('Failed to refresh token:', tokenResp.error);
                    if (['Invalid refresh token', 'No refresh token found'].includes(tokenResp.error)) {
                        keepRetryingRef.current = false;
                        return;
                    }
                } else if (tokenResp?.data) {
                    console.log('Token refreshed successfully.');
                    tokenRef.current = tokenResp.data;
                    connect(tokenRef.current);
                }
            } catch (error) {
                console.error('Error during token refresh:', error);
            } finally {
                reconnectDelayRef.current = Math.min(
                    reconnectDelayRef.current * 2,
                    MAX_RECONNECT_DELAY
                );
            }
        }, reconnectDelayRef.current);
    };

    useEffect(() => {
        let timeoutId;

        const attemptConnect = () => {
            const currentToken = localStorage.getItem('token');
            if (currentToken) {
                tokenRef.current = currentToken;
                connect(currentToken);
            } else {
                console.log('Token not ready, retrying in 500ms...');
                timeoutId = setTimeout(attemptConnect, 500);
            }
        };

        if (chatUser) {
            attemptConnect();
        }

        const handleStorageChange = (event) => {
            if (event.key === 'token') {
                console.log('Detected token change in localStorage.');
                tokenRef.current = event.newValue;
                if (webSocketRef.current) {
                    webSocketRef.current.close(); // Triggers reconnect
                }
            }
        };

        window.addEventListener('storage', handleStorageChange);

        return () => {
            if (webSocketRef.current) {
                webSocketRef.current.close();
            }
            window.removeEventListener('storage', handleStorageChange);
            keepRetryingRef.current = false;
            reconnectDelayRef.current = INITIAL_RECONNECT_DELAY;
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [url, chatUser]);

    return { isConnected, webSocket: webSocketRef.current };
};

export const setupMessageListener = (webSocket, callback) => {
    if (!webSocket) {
        console.error('WebSocket not connected.');
        return;
    }

    webSocket.onmessage = (event) => {
        const data = JSON.parse(event.data);
        callback(data);
    };
};

export default useWS;
